import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Card, CardContent, CardHeader, FormControl, Grid, InputLabel, MenuItem, Select, Switch, TextField, Typography, IconButton, InputAdornment } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { API_ENDPOINTS } from '../config/api';
import { useSnackbar } from 'notistack';

type Environment = 'staging' | 'production' | 'beta' | 'production-new';

interface AccountRow {
  id: string;
  username: string;
  password: string;
  environment: Environment;
  isActive: boolean;
  integration: string;
}

const MAX_ROWS = 12;
const ENV_OPTIONS: Environment[] = ['staging', 'production', 'beta', 'production-new'];
const INTEGRATIONS = ['square', 'toast', 'lightspeed', 'clover', 'non-integration'];

export default function AccountManager() {
  const { t } = useTranslation();
  const [accounts, setAccounts] = useState<AccountRow[]>(() => {
    const init: AccountRow[] = [];
    for (let i = 0; i < MAX_ROWS; i++) {
      init.push({
        id: `init_${i}_${Date.now()}`,
        username: '',
        password: '',
        environment: 'staging',
        isActive: true,
        integration: INTEGRATIONS[0],
      });
    }
    return init;
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [passwordVisibility, setPasswordVisibility] = useState<boolean[]>(() => 
    new Array(MAX_ROWS).fill(false)
  );
  const { enqueueSnackbar } = useSnackbar();

  const fetchAccounts = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_ENDPOINTS.ACCOUNTS_ALL}?t=${Date.now()}`);
      if (res.ok) {
        const data = await res.json();
        const rows: AccountRow[] = (Array.isArray(data) ? data : []).slice(0, MAX_ROWS).map((a: any) => ({
          id: a.id,
          username: a.username,
          password: a.password,
          environment: a.environment as Environment,
          isActive: a.isActive !== false,
          integration: a.integration || 'square',
        }));
        const padded = [...rows];
        while (padded.length < MAX_ROWS) {
          padded.push({
            id: `new_${padded.length}_${Date.now()}`,
            username: '',
            password: '',
            environment: 'staging',
            isActive: true,
            integration: 'square',
          });
        }
        setAccounts(padded);
      }
    } catch (e) {
      console.error(t('accountManager.errors.loadFailed'), e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAccounts();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const firstInvalid = accounts.find(a => (a.username?.trim() || a.password?.trim()) && !emailRegex.test(a.username.trim()));
      if (firstInvalid) {
        enqueueSnackbar(`${t('accountManager.errors.invalidEmail')}: ${firstInvalid.username}`, { variant: 'error' });
        setSaving(false);
        return;
      }
      const payload = {
        accounts: accounts
          .filter(a => (a.username?.trim() || a.password?.trim()))
          .map((a, idx) => ({
            id: a.id || `acc_${Date.now()}_${idx}`,
            username: a.username.trim(),
            password: a.password,
            environment: ENV_OPTIONS.includes(a.environment) ? a.environment : 'staging',
            isActive: a.isActive !== false,
            integration: a.integration,
          })),
      };
      const res = await fetch(`${API_ENDPOINTS.ACCOUNTS_BULK}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || t('accountManager.errors.saveFailed'));
      }
      enqueueSnackbar(t('accountManager.messages.saveSuccess'), { variant: 'success' });
      // 保持当前本地编辑内容不变，避免刷新为空造成"清空"的体验。
      // 如需查看后端落库结果，可点击 Refresh 主动拉取。
    } catch (e) {
      console.error(t('accountManager.errors.saveFailed'), e);
      enqueueSnackbar(t('accountManager.messages.saveFailed'), { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const updateCell = (index: number, key: keyof AccountRow, value: any) => {
    setAccounts(prev => {
      const next = [...prev];
      next[index] = { ...next[index], [key]: value } as AccountRow;
      return next;
    });
  };

  const togglePasswordVisibility = (index: number) => {
    setPasswordVisibility(prev => {
      const next = [...prev];
      next[index] = !next[index];
      return next;
    });
  };

  const title = useMemo(() => t('accountManager.title'), [t]);

  return (
    <Box p={2}>
      <Card>
        <CardHeader
          title={title}
          subheader={t('accountManager.description')}
          action={null}
        />
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Grid container spacing={1}>
                <Grid item xs={4}><Typography variant="subtitle2">{t('accountManager.columns.email')}</Typography></Grid>
                <Grid item xs={3}><Typography variant="subtitle2">{t('accountManager.columns.password')}</Typography></Grid>
                <Grid item xs={2}><Typography variant="subtitle2">{t('accountManager.columns.environment')}</Typography></Grid>
                <Grid item xs={2}><Typography variant="subtitle2">{t('accountManager.columns.integration')}</Typography></Grid>
                <Grid item xs={1}><Typography variant="subtitle2">{t('accountManager.columns.active')}</Typography></Grid>
              </Grid>
            </Grid>
            {accounts.map((row, idx) => (
              <Grid item xs={12} key={row.id || idx}>
                <Grid container spacing={1} alignItems="center">
                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder={t('accountManager.placeholders.email')}
                      value={row.username}
                      onChange={(e) => updateCell(idx, 'username', e.target.value)}
                      error={!row.username.trim() && !!row.password.trim()}
                      helperText={!row.username.trim() && !!row.password.trim() ? t('accountManager.helpers.fillEmail') : ''}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder={t('accountManager.placeholders.password')}
                      type={passwordVisibility[idx] ? 'text' : 'password'}
                      value={row.password}
                      onChange={(e) => updateCell(idx, 'password', e.target.value)}
                      error={!row.password.trim() && !!row.username.trim()}
                      helperText={!row.password.trim() && !!row.username.trim() ? t('accountManager.helpers.fillPassword') : ''}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={() => togglePasswordVisibility(idx)}
                              edge="end"
                              size="small"
                            >
                              {passwordVisibility[idx] ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <FormControl fullWidth size="small">
                      <Select
                        value={row.environment}
                        onChange={(e) => updateCell(idx, 'environment', e.target.value as Environment)}
                      >
                        <MenuItem value="staging">{t('accountManager.environments.staging')}</MenuItem>
                        <MenuItem value="production">{t('accountManager.environments.production')}</MenuItem>
                        <MenuItem value="beta">{t('accountManager.environments.beta')}</MenuItem>
                        <MenuItem value="production-new">{t('accountManager.environments.productionNew')}</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={2}>
                    <FormControl fullWidth size="small">
                      <Select
                        value={row.integration}
                        onChange={(e) => updateCell(idx, 'integration', e.target.value as string)}
                      >
                        {INTEGRATIONS.map(opt => (
                          <MenuItem key={opt} value={opt}>
                            {t(`accountManager.integrations.${opt.replace('-', '')}`, opt)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={1}>
                    <Switch
                      checked={row.isActive}
                      onChange={(e) => updateCell(idx, 'isActive', e.target.checked)}
                    />
                  </Grid>
                </Grid>
              </Grid>
            ))}
          </Grid>
          <Box mt={2} display="flex" justifyContent="flex-end" gap={2}>
            <Button variant="outlined" onClick={() => fetchAccounts()} disabled={loading || saving}>
              {t('accountManager.buttons.refresh')}
            </Button>
            <Button variant="contained" onClick={save} disabled={loading || saving}>
              {t('accountManager.buttons.save')}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}


